import { EventEmitter, Output } from '@angular/core';
import { Component, OnInit, Input } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ICustomer, IUser } from 'app/models';
import { ROLE } from 'app/models/role.model';

import { UserService } from 'app/services';
import { MessageService } from 'app/services/message/message.service';

@Component({
  selector: 'app-user-mngt-detail',
  templateUrl: './user-mngt-detail.component.html',
  styleUrls: ['./user-mngt-detail.component.scss'],
})
export class UserMngtDetailComponent implements OnInit {
  @Input()
  customers: ICustomer[] = [];

  @Output()
  userSaved = new EventEmitter<boolean>();

  roles: ROLE[] = [ROLE.USER, ROLE.ADMIN, ROLE.JOB_MANAGER, ROLE.ROOT];

  userForm: FormGroup = this.formBuilder.group({
    id: undefined,
    username: [
      '',
      {
        validators: [Validators.required],
      },
    ],

    customerId: ['', Validators.required],
    email: ['', Validators.required],
    enabled: [false],
    roles: ['', Validators.required],
  });

  usernames!: string[];

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.userService.getIUsers().subscribe(
      (users) => {
        this.usernames = users.map((item) => item.username);
        this.initEmpty();
      },
      () => this.messageService.showDirectSimpleMessage(` Loading users error `)
    );
  }

  uniqueUsernameValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const id = control.get('id')?.value;
    const username = control.get('username')?.value;

    return !id && this.usernames.includes(username)
      ? { usernameTaken: true }
      : null;
  };

  initEmpty() {
    this.userForm = this.formBuilder.group(
      {
        id: undefined,
        username: [
          null,
          {
            validators: [Validators.required, Validators.minLength(8)],
          },
        ],

        customerId: [null, Validators.required],
        email: [null, Validators.required],
        enabled: [false, Validators.required],
        roles: [null, Validators.required],
      },
      { validators: this.uniqueUsernameValidator }
    );
  }

  initEdit(currentUser: IUser) {
    this.userForm.setValue({
      id: currentUser.id,
      username: currentUser.username,
      customerId: currentUser.customerId,
      email: currentUser.email,
      enabled: currentUser.enabled,
      roles: currentUser.roles,
    });
    this.userForm.controls.username.disable();
  }

  clear(): void {
    this.userForm.controls.username.enable();
    this.resetForm();
  }

  onSubmit() {
    if (!this.userForm) {
      return;
    }
    if (this.userForm.invalid) {
      return;
    }
    if (this.userForm.value.id) {
      this.userService.updateIUser(this.userForm.getRawValue()).subscribe(
        (): void => {
          this.userSaved.emit(true);
          this.clear();
        },
        () => this.messageService.showDirectSimpleMessage(` User save Error`)
      );
      return;
    }
    this.userService.addIUser(this.userForm.getRawValue()).subscribe(
      (): void => {
        this.userSaved.emit(true);
        this.clear();
      },
      () => this.messageService.showDirectSimpleMessage(` User save Error`)
    );
  }

  private resetForm() {
    this.userForm.reset();
  }
}
